import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    Cell,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const NetProfitOverTimeChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];
        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                const net = metrics?.net ?? 0;
                return { date, net };
            });
    }, [finance?.dailyBreakdown]);

    const totalNet = useMemo(
        () => chartData.reduce((sum, d) => sum + d.net, 0),
        [chartData]
    );

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({
        isEmpty,
        summary: !isEmpty ? (
            <span style={{ color: totalNet >= 0 ? CHART_COLORS.revenue : CHART_COLORS.expense }}>
                {formatCompactNumber(totalNet, currencySymbol)}
            </span>
        ) : undefined,
    });

    if (isEmpty) return null;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                <CartesianGrid
                    vertical={false}
                    stroke={CHART_STYLES.grid.stroke}
                    strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                    strokeWidth={CHART_STYLES.grid.strokeWidth}
                    opacity={CHART_STYLES.grid.opacity}
                />
                <XAxis
                    dataKey="date"
                    tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                    {...CHART_STYLES.axis}
                />
                <YAxis
                    tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                    {...CHART_STYLES.axis}
                />
                <Tooltip
                    contentStyle={CHART_STYLES.tooltip.contentStyle}
                    cursor={CHART_STYLES.tooltip.cursor}
                    itemStyle={CHART_STYLES.tooltip.itemStyle}
                    formatter={(value: number) => [formatCurrency(value, currencySymbol), t('charts.netProfit', 'Net Profit')]}
                    labelFormatter={(label: string) => format(parseISO(label), 'MMM d, yyyy')}
                />
                <ReferenceLine y={0} stroke={CHART_COLORS.gray[400]} strokeDasharray="3 3" />
                <Bar
                    dataKey="net"
                    name={t('charts.netProfit', 'Net Profit')}
                    radius={CHART_STYLES.bar.radius}
                    maxBarSize={CHART_STYLES.bar.maxBarSize}
                >
                    {chartData.map((entry, index) => (
                        <Cell
                            key={`cell-${index}`}
                            fill={entry.net >= 0 ? CHART_COLORS.revenue : CHART_COLORS.expense}
                        />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};
